import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createShadowSettingsLegacy } from '../common/schemas/legacy/shadow';
import { createThemeButtonDataLegacy, createThemeButtonHoverLegacy } from '../common/schemas/legacy/theme-button';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';

import type { LegacySocial, LegacySocialDefault, LegacySocialZero, SocialEntity, SocialItem } from './schemas';
import { createSocialItem } from './schemas';

/**
 * Which legacy placement the factory should produce — corresponds to the
 * envelope's `type` discriminator.
 *  - `'default'` (or omitted): a social widget dropped into a normal column.
 *    The persisted envelope omits the `type` field entirely for byte-identical
 *    parity with pre-discriminator data on disk. Data carries the simple
 *    per-device position grid and `isSynchronizeStyle: true`.
 *  - `'zero'`: a social widget dropped into a zero block. Envelope carries
 *    `type: 'zero'`; data uses the extended-position grid (sized to a single
 *    48px icon) and `isSynchronizeStyle` starts `false` (zero-block widgets
 *    are always per-device).
 */
export type LegacySocialVariant = 'default' | 'zero';

/**
 * Per-variant creation knobs. Unlike button (i18n label, generated
 * `elementId`, icon SVG) the social widget's seeded item is fully described
 * by package-owned defaults, so the host app normally passes nothing here.
 */
export type CreateLegacySocialOptions = {
    socialOverrides?: Partial<SocialItem>;
};

const createCommonData = (options?: CreateLegacySocialOptions): Omit<LegacySocialDefault['data'], 'position'> => ({
    socials: [createSocialItem(options?.socialOverrides)],
    socialNetwork: 'telegram',
    type: 'icon',
    iconType: 'round',
    styleType: 'secondary',
    horizontalAlign: 'L',
    mobileHorizontalAlign: 'L',
    tabletHorizontalAlign: 'L',
    height: 48,
    buttonSize: 'M',
    buttonBase: createThemeButtonDataLegacy(),
    buttonHover: createThemeButtonHoverLegacy(),
    isIndependentCorners: false,
    isSynchronizeStyle: true,
    advancedRadius: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: createShadowSettingsLegacy(),
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
});

const createDefaultData = (options?: CreateLegacySocialOptions): LegacySocialDefault['data'] => ({
    ...createCommonData(options),
    position: createWidgetPositionLegacy(),
});

/**
 * Default zero-block dimensions — mirrors the values the host-app form
 * factory passes into `webWidgetExtendedPositionSettingsFormGroup` for the
 * zero social variant (48×48 on every device — one icon; the view DOM-syncs
 * the real rendered size back into the grid).
 */
const createZeroData = (options?: CreateLegacySocialOptions): LegacySocialZero['data'] => ({
    ...createCommonData(options),
    isSynchronizeStyle: false,
    position: createExtendedPositionLegacy({
        width: 48,
        tabletWidth: 48,
        mobileWidth: 48,
        height: 48,
        tabletHeight: 48,
        mobileHeight: 48,
    }),
});

/**
 * Factory for the current legacy social entity — matches the persisted widget
 * JSON shape: `{ id, name: 'social-networks', columnId, data, … }`. Variant
 * selects between default-column placement (no `type` field, simple position
 * grid) and zero-block placement (`type: 'zero'`, extended-position grid).
 *
 * When the redesigned canonical `default/v0` lands, add a sibling
 * `createDefaultSocial` returning that shape; the registry will dispatch
 * between them on a discriminator field.
 */
export function createLegacySocial(variant?: 'default', options?: CreateLegacySocialOptions): LegacySocialDefault;
export function createLegacySocial(variant: 'zero', options?: CreateLegacySocialOptions): LegacySocialZero;
export function createLegacySocial(
    variant: LegacySocialVariant = 'default',
    options?: CreateLegacySocialOptions
): LegacySocial {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'social-networks',
            columnId: '',
            type: 'zero',
            data: createZeroData(options),
        };
    }

    return {
        id: '',
        name: 'social-networks',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new social widgets. While only `legacy` exists, this delegates to
 * {@link createLegacySocial}; pass the variant to opt into the zero-block tag.
 */
export function createSocial(variant?: 'default', options?: CreateLegacySocialOptions): LegacySocialDefault;
export function createSocial(variant: 'zero', options?: CreateLegacySocialOptions): LegacySocialZero;
export function createSocial(variant?: LegacySocialVariant, options?: CreateLegacySocialOptions): SocialEntity {
    // `as any` because the overload signatures resolve at the call site; the
    // implementation just forwards the variant.
    return createLegacySocial(variant as any, options);
}
